import type { ReactNode } from "react";

/**
 * One titled block of a settings page. Every settings screen is a stack of
 * these, so the heading level (2 for the page, 3 for a section) is fixed here
 * rather than chosen per screen.
 */
export function SettingsSection({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  /** Rendered opposite the title: a primary button, a link, a count. */
  actions?: ReactNode;
  children?: ReactNode;
}): ReactNode {
  return (
    <section className="mb-10">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold tracking-wide text-muted uppercase">{title}</h3>
          {description !== undefined && <p className="mt-1 text-sm text-muted">{description}</p>}
        </div>
        {actions}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

/** The page title and lede shared by every settings screen. */
export function SettingsPage({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description: string;
  actions?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="max-w-3xl">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">{title}</h2>
          <p className="mt-1 text-sm text-muted">{description}</p>
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}
